import { formatDateToDisplay, formatDateToISOString } from '../../shared/helpers/date.func';
import {
	ApiActiveAssignment,
	ApiAssignment,
	ApiAssignmentEndOfCare,
	ApiAssignmentWithRelations,
	ApiAssingmentImpedimentReportMinMaxDueDate,
	ApiUserClientAssignment,
	CreateApiAssignmentBody,
	UpdateApiAssignmentBody,
} from '../api-types/assignment.api';
import {
	ActiveAssignment,
	Assignment,
	AssignmentEndOfCare,
	AssignmentWithRelations,
	AssingmentImpedimentReportMinMaxDueDate,
	CreateAssignmentBody,
	UpdateAssignmentBody,
	UserClientAssignment,
} from '../app-types/assignment';
import { DB_PHASES } from '../app-types/phase.app';
import { transformApiCaseGroupToAppCaseGroup } from './case-group.transform';
import { transformApiContactsToAppContacts, transformAppContactsToApiContacts } from './contact.transform';
import { transformApiDisabilityKindToAppDisabilityKind } from './disability-type.transform';
import { transformApiLegalFoundationToAppLegalFoundation } from './legal-foundation.transform';
import { transformApiSponsorToAppSponsor } from './sponsor.transform';
import { transformApiTypeOfCareToAppTypeOfCare } from './type-of-care.transform';
import { transformApiUserToAppUser } from './user.transform';

const hasAssignmentRelations = (input: ApiAssignment): input is ApiAssignmentWithRelations => {
	const withRelations = input as ApiAssignmentWithRelations;
	return (
		withRelations.categoryTypeOfCare !== undefined ||
		withRelations.sponsor !== undefined ||
		withRelations.legalFoundation !== undefined ||
		withRelations.categoryCaseGroup !== undefined ||
		withRelations.careRelatedHandycap !== undefined
	);
};

const transformApiNestedAssignmentToAppAssignment = (input: ApiAssignment): Assignment => {
	return hasAssignmentRelations(input)
		? transformApiAssignmentWithRelationsToAppAssignmentWithRelations(input)
		: transformApiAssignmentToAppAssignment(input);
};

/**
 * Transforms an ApiAssignment object to an app-specific Assignment object.
 * @description This function maps properties of the ApiAssignment to the Assignment interface.
 * It is used to convert data received from the API into a format that the application can work with.
 * @param input ApiAssignment
 * @returns Assignment
 */
export const transformApiAssignmentToAppAssignment = (input: ApiAssignment) => {
	const withRelations = input as ApiAssignmentWithRelations;
	const phaseId = input.phaseId ?? input.phase?.id ?? DB_PHASES.P0;
	const assignmentId = input.id ?? input.assignmentId;
	const contacts = input.contacts ? transformApiContactsToAppContacts(input.contacts) : [];
	const typeOfCareId = input.categoryTypeOfCareId ?? input.categoryTypeOfCare?.id ?? undefined;

	// The backend flag is authoritative, including for nested assignments.
	// Preserve the legacy null-field detection only for responses without the flag.
	const isRestricted =
		input.isRestricted ??
		(!input.isAdditionalAssignment &&
			!input.phase &&
			!input.contactStart &&
			!input.cancellationDate &&
			!input.dueAssignmentImpediment &&
			!input.sponsorId &&
			!input.initiatorId &&
			!input.legalFoundationId &&
			!input.endOfCare &&
			!input.categoryCaseGroupId &&
			!input.careRelatedHandycapId &&
			contacts.length === 0);

	const output: Assignment = {
		id: assignmentId,
		guid: input.guid ?? input.assignmentGuid,
		isServiceOfficeStatement: input.isServiceOfficeStatement,
		isAdditionalAssignment: input.isAdditionalAssignment,
		createdByUser: input.assignedUser ? transformApiUserToAppUser(input.assignedUser) : undefined,
		ownerOfAdditionalAssignment: input.ownerOfAdditionalAssignment
			? transformApiUserToAppUser(input.ownerOfAdditionalAssignment)
			: undefined,
		additionalAssignments: input.additionalAssignments
			? input.additionalAssignments.map(transformApiNestedAssignmentToAppAssignment)
			: [],
		phaseId: phaseId,
		phase: input.phase ?? { id: phaseId, remark: '' },
		phaseStart: input.phaseStart ?? '',
		careRelatedHandicapId: input.careRelatedHandycapId ?? undefined,
		caseGroupId: input.categoryCaseGroupId ?? undefined,
		consultationDate: input.startOfConsultation ? new Date(input.startOfConsultation) : undefined,
		clientId: input.clientId,
		documents:
			input.documents?.map((doc) => ({
				id: doc.fileId,
				name: doc.fileName,
				location: doc.url,
				mimeType: doc.mimeType,
				size: doc.fileByteLength,
				isFrozen: doc.isFrozen,
			})) ?? [],
		impedimentReportDueDate: input.dueAssignmentImpediment ? new Date(input.dueAssignmentImpediment) : undefined,
		careStartDate: input.startOfCare ? new Date(input.startOfCare) : undefined,
		cancellationDate:
			input.cancellationDate && !input.cancellationDate.includes('0001-01-01')
				? new Date(input.cancellationDate)
				: undefined,
		contacts: contacts,
		contactsCount: input.contactsCount ?? contacts.length,
		initiatorId: input.initiatorId ?? undefined,
		legalFoundationId: input.legalFoundationId ?? undefined,
		otherText: input.otherText ?? '',
		socialDataProtectionInfo: input.socialDataProtectionInfo ?? false,
		sponsorId: input.sponsorId ?? undefined,
		typeOfCareId: typeOfCareId,
		unemployabilitySince:
			input.unemployabilitySince && !input.unemployabilitySince.includes('0001-01-01')
				? new Date(input.unemployabilitySince)
				: undefined,
		contactStart: input.contactStart ? new Date(input.contactStart) : undefined,
		phaseChangeId: input.transferPhaseId,
		draftPhaseId: input.draftPhaseId,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt ? new Date(input.clarifiedAt) : undefined,
		typeOfClarification: input.typeOfClarification,

		isRestricted: isRestricted,
	};

	if (withRelations.categoryTypeOfCare) {
		(output as AssignmentWithRelations).typeOfCare = transformApiTypeOfCareToAppTypeOfCare(
			withRelations.categoryTypeOfCare,
		);
	}

	return output;
};

/**
 * Transforms an array of ApiAssignment objects to an array of app-specific Assignment objects.
 * @description This function is useful when you need to convert multiple Assignments received from the API
 * into a format that the application can work with. It uses the `transformApiAssignmentToAppAssignment`
 * function to transform each individual Assignment.
 * @param input Array of ApiAssignment objects.
 * @returns Array of Assignment objects.
 */
export const transformApiAssignmentsToAppAssignments = (input: ApiAssignment[]) => {
	return input.map(transformApiAssignmentToAppAssignment);
};

/**
 * Transforms an app-specific Assignment object to an ApiAssignment object.
 * @description This function maps properties of the Assignment to the ApiAssignment interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @param input Assignment
 * @returns ApiAssignment
 */
export const transformAppAssignmentToApiAssignment = (input: Assignment) => {
	const output: ApiAssignment = {
		id: input.id,
		isServiceOfficeStatement: input.isServiceOfficeStatement,
		isAdditionalAssignment: input.isAdditionalAssignment,
		additionalAssignments: input.additionalAssignments
			? input.additionalAssignments.map(transformAppAssignmentToApiAssignment)
			: [],
		phaseId: input.phaseId,
		phase: input.phase,
		phaseStart: input.phaseStart,
		dueAssignmentImpediment: input.impedimentReportDueDate?.getISODateStringWithoutTime(),
		careRelatedHandycapId: input.careRelatedHandicapId,
		startOfConsultation: input.consultationDate?.getISODateStringWithoutTime(),
		startOfCare: input.careStartDate?.getISODateStringWithoutTime(),
		categoryCaseGroupId: input.caseGroupId,
		cancellationDate: input.cancellationDate?.getISODateStringWithoutTime(),
		categoryTypeOfCareId: input.typeOfCareId,
		contactStart: input.contactStart?.getISODateStringWithoutTime() ?? '',
		initiatorId: input.initiatorId,
		clientId: input.clientId,
		endOfCare: input.endOfCare
			? {
					id: input.endOfCare.id,
					designation: input.endOfCare.designation,
					activityEnforcedStatus: input.endOfCare.activityEnforcedStatus,
					initials: input.endOfCare.initials,
					endOfCareGroup: input.endOfCare.endOfCareGroup
						? {
								id: input.endOfCare.endOfCareGroup.id,
								designation: input.endOfCare.endOfCareGroup.designation,
								initials: input.endOfCare.endOfCareGroup.initials,
							}
						: undefined,
				}
			: undefined,
		contacts: transformAppContactsToApiContacts(input.contacts),
		legalFoundationId: input.legalFoundationId,
		otherText: input.otherText,
		socialDataProtectionInfo: input.socialDataProtectionInfo,
		sponsorId: input.sponsorId,
		unemployabilitySince: input.unemployabilitySince?.toISOString(),
		transferPhaseId: input.phaseChangeId,
		documents:
			input.documents?.map((doc) => ({
				fileId: doc.id,
				fileName: doc.name,
				mimeType: doc.mimeType,
				url: doc.location,
				fileByteLength: doc.size,
				isFrozen: doc.isFrozen,
			})) ?? [],
		draftPhaseId: input.draftPhaseId,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt?.getISODateStringWithoutTime(),
		typeOfClarification: input.typeOfClarification,
	};
	return output;
};

/**
 * Transform the CreateAssignment object to a backend-specific Assignment object
 * @param input CreateAssignment
 * @description This function maps the properties of the CreateAssignment to the CreateApiAssignmentBody.
 * It is used to convert data to be sent to the API into a format that the backend can work with
 * @returns CreateApiAssignmentBody
 */
export const transformAppCreateAssignmentToApiCreateAssignment = (input: CreateAssignmentBody) => {
	const output: CreateApiAssignmentBody = {
		phaseId: input.phaseId,
		phase: input.phase,
		phaseStart: input.phaseStart,
		categoryTypeOfCareId: input.typeOfCareId,
		sponsorId: input.sponsorId,
		legalFoundationId: input.legalFoundationId,
		categoryCaseGroupId: input.caseGroupId,
		careRelatedHandycapId: input.careRelatedHandicapId,
		socialDataProtectionInfo: input.socialDataProtectionInfo,
		initiatorId: input.initiatorId,
		endOfCareId: input.endOfCareId,
		unemployabilitySince: formatDateToISOString(input.unemployabilitySince),
		cancellationDate: formatDateToISOString(input.cancellationDate),
		startOfConsultation: formatDateToISOString(input.consultationStartDate),
		startOfCare: formatDateToISOString(input.careStartDate),
		otherText: input.otherText,
		contactStart: input.contactStart!,
		documentIds: input.documentIds,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: formatDateToISOString(input.clarifiedAt),
		typeOfClarification: input.typeOfClarification,
	};

	return output;
};

export const transformAppAssignmentToCreateAssignment = (input: Assignment): CreateAssignmentBody => {
	const output: CreateAssignmentBody = {
		phaseId: input.phaseId,
		phase: input.phase,
		cancellationDate: input.cancellationDate ? new Date(input.cancellationDate) : undefined,
		careStartDate: input.careStartDate,
		phaseStart: input.phaseStart,
		typeOfCareId: input.typeOfCareId,
		sponsorId: input.sponsorId,
		legalFoundationId: input.legalFoundationId,
		careRelatedHandicapId: input.careRelatedHandicapId,
		consultationStartDate: input.consultationDate,
		caseGroupId: input.caseGroupId,
		socialDataProtectionInfo: input.socialDataProtectionInfo,
		initiatorId: input.initiatorId,
		unemployabilitySince: input.unemployabilitySince ? new Date(input.unemployabilitySince) : undefined,
		otherText: input.otherText,
		endOfCareId: input.endOfCare?.id,
		contactStart: input.contactStart?.getISODateStringWithoutTime(),
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt ? new Date(input.clarifiedAt) : undefined,
		typeOfClarification: input.typeOfClarification,
	};
	return output;
};

export const transformApiAssignmentWithRelationsToAppAssignmentWithRelations = (
	input: ApiAssignmentWithRelations,
): AssignmentWithRelations => {
	const output: AssignmentWithRelations = {
		...transformApiAssignmentToAppAssignment(input),
		additionalAssignments: input.additionalAssignments
			? input.additionalAssignments.map(transformApiNestedAssignmentToAppAssignment)
			: [],
		typeOfCare: input.categoryTypeOfCare ? transformApiTypeOfCareToAppTypeOfCare(input.categoryTypeOfCare) : undefined,
		sponsor: input.sponsor ? transformApiSponsorToAppSponsor(input.sponsor) : undefined,
		legalFoundation: input.legalFoundation
			? transformApiLegalFoundationToAppLegalFoundation(input.legalFoundation)
			: undefined,
		caseGroup: input.categoryCaseGroup ? transformApiCaseGroupToAppCaseGroup(input.categoryCaseGroup) : undefined,
		careRelatedHandicap: input.careRelatedHandycap
			? transformApiDisabilityKindToAppDisabilityKind(input.careRelatedHandycap)
			: undefined,
		endOfCare: input.endOfCare ? transformApiEndOfCareToAppEndOfCare(input.endOfCare) : undefined,
	};

	return output;
};

export const transformUpdateAssignmentBodyToApiUpdateAssignmentBody = (
	input: UpdateAssignmentBody,
): UpdateApiAssignmentBody => {
	const output: UpdateApiAssignmentBody = {
		phaseId: input.phaseId,
		transferPhaseId: input.phaseChangeId,
		cancellationDate: input.cancellationDate?.getISODateStringWithoutTime(),
		startOfConsultation: input?.consultationStartDate?.getISODateStringWithoutTime(),
		startOfCare: input.careStartDate?.getISODateStringWithoutTime(),
		categoryTypeOfCareId: input.typeOfCareId,
		sponsorId: input.sponsorId,
		legalFoundationId: input.legalFoundationId,
		categoryCaseGroupId: input.caseGroupId,
		careRelatedHandycapId: input.careRelatedHandicapId,
		socialDataProtectionInfo: input.socialDataProtectionInfo,
		initiatorId: input.initiatorId,
		endOfCareId: input.endOfCareId,
		unemployabilitySince: input.unemployabilitySince?.getISODateStringWithoutTime(),
		otherText: input.otherText,
		documentIds: input.documentIds,
		dueAssignmentImpediment: input.impedimentReportDueDate?.getISODateStringWithoutTime(),
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt?.getISODateStringWithoutTime(),
		typeOfClarification: input.typeOfClarification,
	};
	return output;
};

export const transformAssignmentToUpdateAssignmentBody = (input: Assignment): UpdateAssignmentBody => {
	const output: UpdateAssignmentBody = {
		phaseId: input.phaseId,
		phaseChangeId: input.phaseChangeId,
		cancellationDate: input.cancellationDate ? new Date(input.cancellationDate) : undefined,
		careStartDate: input.careStartDate ? new Date(input.careStartDate) : undefined,
		consultationStartDate: input.consultationDate ? new Date(input.consultationDate) : undefined,
		initiatorId: input.initiatorId,
		typeOfCareId: input.typeOfCareId,
		sponsorId: input.sponsorId,
		legalFoundationId: input.legalFoundationId,
		caseGroupId: input.caseGroupId,
		careRelatedHandicapId: input.careRelatedHandicapId,
		socialDataProtectionInfo: input.socialDataProtectionInfo,
		unemployabilitySince: input.unemployabilitySince ? new Date(input.unemployabilitySince) : undefined,
		otherText: input.otherText,
		endOfCareId: input.endOfCare?.id,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt ? new Date(input.clarifiedAt) : undefined,
		typeOfClarification: input.typeOfClarification,
	};
	return output;
};

export const transformApiEndOfCareToAppEndOfCare = (input: ApiAssignmentEndOfCare): AssignmentEndOfCare => {
	return {
		id: input.id,
		designation: input.designation,
		activityEnforcedStatus: input.activityEnforcedStatus,
		initials: input.initials,
		endOfCareGroup: input.endOfCareGroup
			? {
					id: input.endOfCareGroup.id,
					designation: input.endOfCareGroup.designation,
					initials: input.endOfCareGroup.initials,
				}
			: undefined,
	};
};

export const transformApiEndOfCaresToAppEndOfCares = (input: ApiAssignmentEndOfCare[]): AssignmentEndOfCare[] => {
	return input.map(transformApiEndOfCareToAppEndOfCare);
};

/**
 * Maps the name of a control in our form to the corresponding field name expected by the API for assignments.
 * @param fieldName Name of the field inside our forms
 * @returns name of the field coming from the API
 */
export const mapEditFormFieldNamesToApiAssignmentFieldNames = (fieldName: string): string => {
	const fieldMapping: Record<string, string> = {
		typeOfCareId: 'CategoryTypeOfCare',
		sponsorId: 'Sponsor',
		legalFoundationId: 'LegalFoundation',
		caseGroupId: 'CategoryCaseGroup',
		careRelatedHandicapId: 'CareRelatedHandycap',
		socialDataProtectionInfo: 'socialDataProtectionInfo',
		clarification: 'SocialDataProtectionInfo',
		initiatorId: 'initiatorId',
		otherText: 'otherText',
		consultationStartDate: 'StartOfConsultation',
		careStartDate: 'StartOfCare',
		dateOfCommission: 'StartOfCare',
		cancellationDate: 'CancellationDate',
		unemployabilitySince: 'UnemployabilitySince',
		endOfCareId: 'EndOfCare',
		impedimentReportDueDate: 'DueAssignmentImpediment',
	};
	return fieldMapping[fieldName] || fieldName;
};

export const transformApiActiveAssignmentToAppActiveAssignment = (input: ApiActiveAssignment) => {
	const lastContact = input.lastContact
		? formatDateToDisplay(new Date(formatDateToISOString(input.lastContact)! + 'T12:00:00'))
		: '--';
	const output: ActiveAssignment = {
		activeAssignmentId: input.activeAssignmentId,
		currentPhaseStart: input.currentPhaseStart ? formatDateToDisplay(new Date(input.currentPhaseStart)) : '--',
		lastContact: lastContact === '31.12.1' ? '--' : lastContact,
		phase: input.phase ?? undefined,
		isAdditionalAssignment: input.isAdditionalAssignment,
		isServiceOfficeStatement: input.isServiceOfficeStatement,
		typeOfCare: input.typeOfCare,
		dueDate: input.dueDate ? formatDateToDisplay(new Date(formatDateToISOString(input.dueDate)! + 'T12:00:00')) : '--',
	};
	return output;
};

export const transformApiAssingmentImpedimentReportMinMaxDueDateToAssingmentImpedimentReportMinMaxDueDate = (
	input: ApiAssingmentImpedimentReportMinMaxDueDate,
) => {
	const output: AssingmentImpedimentReportMinMaxDueDate = {
		minStartDate: input.minStartDate ? new Date(input.minStartDate) : undefined,
		maxEndDate: input.maxEndDate ? new Date(input.maxEndDate) : undefined,
	};
	return output;
};

export const transformApiActiveAssignmentTsoAppActiveAssignments = (
	input: ApiActiveAssignment[],
): ActiveAssignment[] => {
	return input.map(transformApiActiveAssignmentToAppActiveAssignment);
};

export const transformApiUserClientAssignmentToAppUserClientAssignment = (input: ApiUserClientAssignment) => {
	const output: UserClientAssignment = {
		id: input.id,
		assignmentGuid: input.assignmentGuid,
		phase: input.phase,
		currentPhaseStart: input.currentPhaseStart,
		ownerFullName: input.ownerFullName,
		assignmentType: input.assignmentType,
		validOperation: input.validOperation,
	};
	return output;
};

export const transformApiUserClientAssignmentsToAppUserClientAssignments = (
	input: ApiUserClientAssignment[],
): UserClientAssignment[] => {
	return input.map(transformApiUserClientAssignmentToAppUserClientAssignment);
};
